import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const northbeam: Competitor = {
  "id": "northbeam",
  "name": "Northbeam",
  "group": "ecomm",
  "website": "https://www.northbeam.io/",
  "owner": "Independent, privately held (North Beam, Inc.); growth investment led by HighPost Capital and Silversmith Capital Partners announced Oct 2025",
  "summary": "Northbeam is a marketing measurement platform for DTC and omnichannel brands that combines a first-party tracking pixel and multi-touch attribution with optional incrementality testing and media mix modeling. It is aimed at brands spending meaningful money on paid media who want an independent view of channel performance.",
  "bestFor": [
    "DTC brands spending roughly $50K+ per month on paid social and search that need cross-channel attribution beyond ad-platform reporting",
    "Marketing teams that want attribution, incrementality tests and MMM from one vendor with a media strategist attached (Professional and Enterprise)",
    "Shopify brands that want a working pixel-based attribution setup in weeks rather than building identity stitching themselves"
  ],
  "notFor": [
    "Brands whose main need is finance-grade P&L, contribution margin or inventory reporting rather than marketing measurement",
    "Wholesale- or retail-heavy brands needing native ERP, EDI or retail POS data blended into one model",
    "Small brands where a starting price around $1,500/month is out of proportion to ad spend"
  ],
  "strengths": [
    "Ships its own first-party pixel with six attribution models and view-through credit (Clicks + Deterministic Views); building comparable identity stitching and touchpoint attribution in a custom Snowflake + dbt stack is a large, ongoing engineering effort",
    "Automated incrementality testing (launched Apr 2026, Meta channel-level in the US at launch) and MMM are available from the same vendor, which a custom warehouse build would have to source separately or build with a data science team",
    "Documented implementation of about 2-4 weeks for Shopify brands, faster than a typical custom warehouse plus attribution build",
    "Professional and Enterprise plans include a dedicated media strategist, so the output comes with interpretation, not just tables",
    "Read-only MCP server and an in-app AI assistant are available on all plans, plus an iOS app for checking ROAS/MER on the go"
  ],
  "limitations": [
    "Amazon integration currently reports Seller Central revenue and Amazon Ads spend only; Northbeam's docs say Amazon attribution is still in progress, and Vendor Central and Amazon DSP are not supported",
    "TikTok Shop and Meta Shop orders are not pulled directly from TikTok; they must sync through Shopify or be tagged and sent via the Orders API",
    "Raw data reaches your own warehouse only as file exports to S3 or GCS that you load yourself; no native Snowflake or BigQuery share is documented, and row-level touchpoint export is limited to select plans",
    "Profit Benchmarks needs COGS you supply; the docs do not describe a contribution margin waterfall with shipping, payment fees or returns",
    "The documented metric list lets users add or remove predefined metrics but does not describe custom formula metrics, so business-specific KPIs are harder than in a custom dbt/Sigma model",
    "Non-Shopify implementations are documented at about 4-8 weeks and depend on your developers",
    "MMM is an optional Enterprise-only component and incrementality is an add-on from Professional upward, so the full measurement suite sits at the top of the price list"
  ],
  "pricing": {
    "model": "Tiered by annual ad spend, with monthly price also tied to data volume (pageviews)",
    "publicStartingPrice": "Starter shown at $1,500/month (month-to-month); Professional shown at $3,500/month (annual term). Northbeam's FAQ calls these starting estimates.",
    "freeTier": "No free tier or free trial listed on the pricing page",
    "notes": "Four tiers: Growth (custom quote, brands under $200K/month ad spend, bundled with agency-style creative and media buying support), Starter ($1,500/month, under $1.5M/year ad spend, Shopify-focused, month-to-month, ticket support), Professional ($3,500/month, up to $500K/month ad spend, unlimited users, data exports, non-Shopify platforms, dedicated media strategist, annual term), Enterprise (custom, above $500K/month ad spend, CSM and Slack channel, granular touchpoint export, optional MMM+). Incrementality is optional on Professional and Enterprise. Price is driven by ad-spend band and pageview volume. Each tier advertises partner ad credits. Setup: Northbeam documents about 2-4 weeks for Shopify brands and 4-8 weeks for non-Shopify brands, with at least 3 fully tracked days required for validation.",
    "sourceUrl": "https://www.northbeam.io/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Shopify app for pixel install and order sync",
      "sourceUrl": "https://docs.northbeam.io/llms.txt"
    },
    "amazon": {
      "value": "partial",
      "note": "Pulls Seller Central total revenue and Amazon Ads spend; attribution for Amazon not yet available; no Vendor Central or DSP",
      "sourceUrl": "https://docs.northbeam.io/docs/channels/amazon/overview.md"
    },
    "tiktok_shop": {
      "value": "partial",
      "note": "Orders must sync via Shopify or be tagged and sent through the Orders API; no direct TikTok Shop order feed documented",
      "sourceUrl": "https://docs.northbeam.io/docs/channels/shops/overview.md"
    },
    "wholesale_retail_or_erp": {
      "value": "unknown",
      "note": "No native ERP, wholesale or POS connector found in docs; custom order sources can be sent via Orders API",
      "sourceUrl": "https://docs.northbeam.io/docs/integration-matrix.md"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Ad account connections (Meta, Google, TikTok and others) plus spend API and spreadsheets for other sources",
      "sourceUrl": "https://docs.northbeam.io/docs/what-to-expect-during-implementation.md"
    },
    "first_party_pixel_attribution": {
      "value": "yes",
      "note": "First-party pixel with DNS setup; six attribution models, Clicks-Only default, Clicks + Deterministic Views available",
      "sourceUrl": "https://docs.northbeam.io/docs/what-is-northbeam"
    },
    "mmm_incrementality": {
      "value": "yes",
      "note": "MMM+ is an optional Enterprise component; incrementality is an add-on from Professional (Meta, US-only at Apr 2026 launch)",
      "sourceUrl": "https://www.northbeam.io/pricing"
    },
    "profit_cogs": {
      "value": "partial",
      "note": "Profit Benchmarks uses COGS you supply; shipping, fees and margin waterfall not documented",
      "sourceUrl": "https://docs.northbeam.io/docs/profitability-benchmarks.md"
    },
    "ltv_cohorts": {
      "value": "unknown",
      "note": "No LTV or cohort doc page found; docs cover first-time vs returning splits only",
      "sourceUrl": "https://docs.northbeam.io/llms.txt"
    },
    "subscription_analytics": {
      "value": "partial",
      "note": "Subscription metrics (first-time vs recurring orders, revenue, refunds) based on order tags; no Recharge connector named",
      "sourceUrl": "https://docs.northbeam.io/docs/subscription-metrics.md"
    },
    "warehouse_access": {
      "value": "partial",
      "note": "Data Export API and touchpoint exports write files to S3/GCS for you to load; no native warehouse share; data exports listed from Professional",
      "sourceUrl": "https://docs.northbeam.io/docs/exporting-data.md"
    },
    "custom_metrics": {
      "value": "unknown",
      "note": "Docs describe toggling predefined metrics and custom goal tracking; custom formula metrics not documented",
      "sourceUrl": "https://docs.northbeam.io/docs/northbeam-metrics-101"
    },
    "custom_sql": {
      "value": "unknown",
      "note": "No in-product SQL editor found in docs",
      "sourceUrl": "https://docs.northbeam.io/llms.txt"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Northbeam AI in-dashboard assistant on all plans (weekly usage limit) plus read-only MCP server for Claude/ChatGPT",
      "sourceUrl": "https://docs.northbeam.io/docs/northbeam-ai.md"
    },
    "mobile_app": {
      "value": "yes",
      "note": "iOS app from North Beam, Inc. for ROAS/MER and saved views",
      "sourceUrl": "https://apps.apple.com/ie/app/northbeam/id6454298852"
    }
  },
  "recentChanges": [
    {
      "date": "2025-10",
      "change": "Launched Clicks + Deterministic Views attribution model for all customers and announced growth investment led by HighPost Capital and Silversmith Capital Partners",
      "sourceUrl": "https://www.businesswire.com/news/home/20251007146281/en"
    },
    {
      "date": "2026-04",
      "change": "Launched Northbeam Incrementality, automated incrementality testing starting with Meta channel-level tests in the US; more channels and regions planned for later in 2026",
      "sourceUrl": "https://www.martechcube.com/?p=297513"
    },
    {
      "date": "2026",
      "change": "Pricing page now lists a Growth tier bundled with agency services and an MCP server on all plans; Northbeam AI assistant and MCP connectors for Claude and ChatGPT documented",
      "sourceUrl": "https://www.northbeam.io/pricing"
    }
  ],
  "sources": [
    {
      "title": "Northbeam - Pricing",
      "url": "https://www.northbeam.io/pricing"
    },
    {
      "title": "What is Northbeam? Start here.",
      "url": "https://docs.northbeam.io/docs/what-is-northbeam"
    },
    {
      "title": "Northbeam docs index (llms.txt)",
      "url": "https://docs.northbeam.io/llms.txt"
    },
    {
      "title": "What To Expect During Implementation",
      "url": "https://docs.northbeam.io/docs/what-to-expect-during-implementation.md"
    },
    {
      "title": "Amazon Setup",
      "url": "https://docs.northbeam.io/docs/channels/amazon/overview.md"
    },
    {
      "title": "Meta & TikTok Shops Setup",
      "url": "https://docs.northbeam.io/docs/channels/shops/overview.md"
    },
    {
      "title": "Which export should I use?",
      "url": "https://docs.northbeam.io/docs/exporting-data.md"
    },
    {
      "title": "Pull Northbeam data into your warehouse",
      "url": "https://docs.northbeam.io/docs/use-cases/pull-data-into-your-warehouse.md"
    },
    {
      "title": "Mixed Media Modeling (MMM)",
      "url": "https://docs.northbeam.io/docs/mixed-media-modeling.md"
    },
    {
      "title": "Northbeam AI",
      "url": "https://docs.northbeam.io/docs/northbeam-ai.md"
    },
    {
      "title": "Subscription Metrics",
      "url": "https://docs.northbeam.io/docs/subscription-metrics.md"
    },
    {
      "title": "Profit Benchmarks",
      "url": "https://docs.northbeam.io/docs/profitability-benchmarks.md"
    },
    {
      "title": "Northbeam Available Metrics",
      "url": "https://docs.northbeam.io/docs/northbeam-metrics-101"
    },
    {
      "title": "Northbeam iOS app (App Store)",
      "url": "https://apps.apple.com/ie/app/northbeam/id6454298852"
    },
    {
      "title": "Business Wire: Clicks + Deterministic Views and growth investment (Oct 2025)",
      "url": "https://www.businesswire.com/news/home/20251007146281/en"
    },
    {
      "title": "MarTech Cube: Northbeam Launches Northbeam Incrementality (Apr 2026)",
      "url": "https://www.martechcube.com/?p=297513"
    }
  ],
  "lastReviewed": "2026-11-03"
};
